import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

import ContestSelfEvalConsentDialog from '@components/contest/ContestSelfEvalConsentDialog';
import ContestResultsCompareDialog, {
  type ContestCompareSkillRow,
} from '@components/contest/ContestResultsCompareDialog';
import { ContestFrameShell, ContestSelfEvalScorecardPanel } from '@components/contest/PublicContestFrames';

import { useFoundationCatalogGroups } from '@hooks/useFoundationCatalogGroups';

import { brandColors } from '@config/brandTokens';

import { CONTEST_TYPE_OPTIONS, hasContestCatalogPreview, isProfileContestAge } from '@utils/contestCatalog';

import {
  assessEntryValue,
  buildFilledCount,
  contestSelfEvalQuarterLabel,
  isContestReadyConfirmed,
  resolveSelfEvalConsentRole,
  type ContestSelfEvalConsentRecord,
  isContestSelfEvalComplete,
  loadContestSelfEvalForGate,
  setContestReadyConfirmed,
  type ContestSelfEvalDraft,
} from '@utils/contestSelfEvaluation';

import {
  CONTEST_TWO_PER_DAY_MESSAGE,
  countContestVideosOnDate,
  fetchPlayerContestSubmissionsByCatalog,
  formatContestAdminScore,
  formatContestSubmittedAt,
  formatSelfAttemptCounts,
  loadContestPlayerNotes,
  MAX_CONTEST_VIDEOS_PER_DATE,
  parseSelfAttemptCounts,
  saveContestPlayerNotes,
  todayIsoDate,
  type PlayerContestSubmissionRow,
} from '@utils/contestSubmissionStatus';

import { buildContestSubmitLoginPath } from '@utils/contestSubmitNavigation';

import {

  fetchContestTasks,

  uploadContestPerformanceVideo,

  type ContestTaskRow,

} from '@utils/contestEntryUpload';



export interface ContestSubmissionTabPanelProps {

  sportName: string;

  ageCategory: string;

  genderKey?: import('../../types/contestCatalog').ContestGenderKey;

  playerId?: string;

  playerName?: string;

  userId?: string;

  isAuthenticated: boolean;

  onLoginRequired: (loginPath: string) => void;

  /** Switch journey tab to Self Evaluation (lean prep path). */
  onGoToSelfEval?: () => void;
  viewOnly?: boolean;
  missingDob?: boolean;
  eligibleAgeCategory?: string;
  profileSlot?: React.ReactNode;
  actingKind?: 'self' | 'child';

}



const ContestSubmissionTabPanel: React.FC<ContestSubmissionTabPanelProps> = ({

  sportName,

  ageCategory,

  genderKey = 'boys',

  playerId,

  playerName,

  userId,

  isAuthenticated,

  onLoginRequired,

  onGoToSelfEval,

  viewOnly = false,

  missingDob = false,

  eligibleAgeCategory,

  profileSlot,

  actingKind,

}) => {

  const { categoryGroups, allContests, loading: catalogLoading, hasContests } =
    useFoundationCatalogGroups(sportName);
  const hasPreview = hasContests || hasContestCatalogPreview(sportName);

  const typeLabel =
    CONTEST_TYPE_OPTIONS.find((o) => o.id === 'foundation')?.label || 'Developmental & Performance Capabilities';
  const foundationLabel = `${typeLabel}${allContests.length ? ` (${allContests.length} skill${allContests.length === 1 ? '' : 's'})` : ''}`;
  const canWriteThisAge = isProfileContestAge(eligibleAgeCategory, ageCategory);
  const ageMismatchMessage = eligibleAgeCategory
    ? `Videos can only be submitted for ${playerName || 'this player'} in ${eligibleAgeCategory}. You can still view ${ageCategory}.`
    : '';

  const contestIds = useMemo(() => allContests.map((c) => c.id), [allContests]);

  const [draft, setDraft] = useState<ContestSelfEvalDraft>(() =>
    loadContestSelfEvalForGate(sportName, ageCategory, playerId || userId),
  );

  const [readyConfirmed, setReadyConfirmed] = useState(() =>
    isContestReadyConfirmed(sportName, ageCategory),
  );

  const [submissionsByCatalog, setSubmissionsByCatalog] = useState<
    Record<string, PlayerContestSubmissionRow>
  >({});

  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});

  const [uploadingContestId, setUploadingContestId] = useState<string | null>(null);

  const [uploadError, setUploadError] = useState<string | null>(null);

  const [contestTasks, setContestTasks] = useState<ContestTaskRow[]>([]);
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingConsentContestId, setPendingConsentContestId] = useState<string | null>(null);
  const [pendingRecordedDates, setPendingRecordedDates] = useState<Record<string, string>>({});
  const [pendingSelfCounts, setPendingSelfCounts] = useState<
    Record<string, { attempts: string; successes: string }>
  >({});
  const [playerNotes, setPlayerNotes] = useState<Record<string, string>>({});
  const [compareOpen, setCompareOpen] = useState(false);



  useEffect(() => {
    setDraft(loadContestSelfEvalForGate(sportName, ageCategory, playerId || userId));
    setReadyConfirmed(isContestReadyConfirmed(sportName, ageCategory));
    setPlayerNotes(loadContestPlayerNotes(sportName, ageCategory, playerId || userId));
  }, [sportName, ageCategory, userId, playerId]);



  useEffect(() => {

    let cancelled = false;

    void fetchContestTasks(sportName)

      .then((rows) => {

        if (!cancelled) setContestTasks(rows);

      })

      .catch(() => {

        if (!cancelled) setContestTasks([]);

      });

    return () => {

      cancelled = true;

    };

  }, [sportName]);



  const refreshSubmitted = useCallback(async () => {
    if (!playerId) {
      setSubmissionsByCatalog({});
      return;
    }
    const rows = await fetchPlayerContestSubmissionsByCatalog(
      sportName,
      playerId,
      allContests,
      ageCategory,
    );
    setSubmissionsByCatalog(rows);
  }, [playerId, sportName, allContests, ageCategory]);



  useEffect(() => {

    void refreshSubmitted();

  }, [refreshSubmitted]);



  const submittedIds = useMemo(
    () => Object.keys(submissionsByCatalog).filter((id) => submissionsByCatalog[id]?.submitted),
    [submissionsByCatalog],
  );

  const submittedVideoUrls = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(submissionsByCatalog)
          .filter(([, row]) => row?.submitted && row.videoUrl)
          .map(([id, row]) => [id, row.videoUrl as string]),
      ),
    [submissionsByCatalog],
  );

  const contestEntries = useMemo(() => {
    const entries: ContestSelfEvalDraft['entries'] &
      Record<string, { submittedDate?: string; totalAttempts?: string; successfulAttempts?: string }> = {};
    allContests.forEach((contest) => {
      const row = submissionsByCatalog[contest.id];
      const value = row ? formatContestAdminScore(row.score, row.status) : '';
      entries[contest.id] = {
        contestId: contest.id,
        value,
        totalAttempts:
          row?.selfAttempts != null
            ? String(row.selfAttempts)
            : pendingSelfCounts[contest.id]?.attempts || '',
        successfulAttempts:
          row?.selfSuccesses != null
            ? String(row.selfSuccesses)
            : pendingSelfCounts[contest.id]?.successes || '',
        recordedDate: row?.recordedDate || pendingRecordedDates[contest.id] || '',
        submittedDate: row?.submitted ? formatContestSubmittedAt(row.createdAt) : '',
        selfNotes: playerNotes[contest.id] || '',
        coachNotes: row?.feedback || '',
        errors: [],
        grade: value ? assessEntryValue(contest, ageCategory, value, genderKey) : null,
      };
    });
    return entries;
  }, [allContests, submissionsByCatalog, pendingRecordedDates, pendingSelfCounts, playerNotes, ageCategory, genderKey]);

  const compareRows = useMemo((): ContestCompareSkillRow[] => {
    return allContests.map((contest) => {
      const selfEntry = draft.entries[contest.id];
      const contestRow = submissionsByCatalog[contest.id];
      const contestValue = contestRow ? formatContestAdminScore(contestRow.score, contestRow.status) : '';
      return {
        title: contest.title,
        selfValue: String(selfEntry?.value || ''),
        selfGrade: selfEntry?.grade ?? null,
        selfDate: String(selfEntry?.recordedDate || ''),
        contestValue,
        contestGrade: contestValue ? assessEntryValue(contest, ageCategory, contestValue, genderKey) : null,
        contestRecordedDate: contestRow?.recordedDate || '',
        contestSubmittedDate: contestRow?.submitted ? formatContestSubmittedAt(contestRow.createdAt) : '',
        contestStatus: contestRow?.status || '',
        contestSelfCounts: formatSelfAttemptCounts(contestRow?.selfAttempts, contestRow?.selfSuccesses),
      };
    });
  }, [allContests, draft.entries, submissionsByCatalog, ageCategory, genderKey]);



  const ensureAuthForContest = (contestId: string): boolean => {
    if (viewOnly) {
      setUploadError(
        playerName
          ? `View only — contest videos for ${playerName} must be submitted by a parent until this Player ID is claimed.`
          : 'View only — a parent must submit contest videos until you claim your email.',
      );
      return false;
    }
    if (missingDob) {
      setUploadError(
        'Add a date of birth on this profile first. Age category is calculated from date of birth.',
      );
      return false;
    }
    if (!canWriteThisAge) {
      setUploadError(ageMismatchMessage);
      return false;
    }

    if (isAuthenticated && playerId) return true;

    onLoginRequired(buildContestSubmitLoginPath(sportName, contestId, ageCategory));

    return false;

  };

  const selfEvalComplete = useMemo(
    () => isContestSelfEvalComplete(draft, contestIds),
    [draft, contestIds],
  );

  const filledCount = useMemo(() => buildFilledCount(draft), [draft]);
  const selfEvalSubmitted = draft.status === 'submitted';
  const contestUnlocked = readyConfirmed || selfEvalSubmitted;
  const canBrowseSubmit = selfEvalComplete && contestUnlocked;

  const confirmReady = () => {
    setContestReadyConfirmed(sportName, ageCategory, true);
    setReadyConfirmed(true);
    setUploadError(null);
  };

  const handleBrowseClick = (contestId: string): boolean => {

    setUploadError(null);

    if (!canBrowseSubmit) {
      setUploadError('Complete Self Evaluation and confirm you are ready before browsing videos.');
      return false;
    }

    return ensureAuthForContest(contestId);

  };



  const handleVideoSelected = (contestId: string, file: File) => {

    if (!canBrowseSubmit) {
      setUploadError('Complete Self Evaluation and confirm you are ready before selecting a video.');
      return;
    }

    if (!ensureAuthForContest(contestId)) return;

    setPendingFiles((prev) => ({ ...prev, [contestId]: file }));

    setUploadError(null);

  };

  const handleRecordedDateChange = (contestId: string, date: string) => {
    const nextDate = String(date || '').trim();
    if (nextDate && nextDate > todayIsoDate()) {
      setUploadError('Recorded date cannot be in the future.');
      return;
    }
    if (
      nextDate &&
      countContestVideosOnDate(submissionsByCatalog, nextDate, contestId) >= MAX_CONTEST_VIDEOS_PER_DATE
    ) {
      setUploadError(CONTEST_TWO_PER_DAY_MESSAGE);
      return;
    }
    setPendingRecordedDates((prev) => ({ ...prev, [contestId]: nextDate }));
    setUploadError(null);
  };

  const handleSelfCountChange = (contestId: string, field: 'attempts' | 'successes', value: string) => {
    const nextValue = value.replace(/[^\d]/g, '');
    setPendingSelfCounts((prev) => ({
      ...prev,
      [contestId]: {
        attempts: field === 'attempts' ? nextValue : prev[contestId]?.attempts || '',
        successes: field === 'successes' ? nextValue : prev[contestId]?.successes || '',
      },
    }));
    setUploadError(null);
  };

  const handleSelfNotesChange = (contestId: string, notes: string) => {
    setPlayerNotes((prev) => {
      const next = { ...prev, [contestId]: notes };
      saveContestPlayerNotes(sportName, ageCategory, playerId || userId, next);
      return next;
    });
  };



  const handleSubmitVideo = (contestId: string) => {
    if (viewOnly) {
      setUploadError(
        playerName
          ? `View only — contest videos for ${playerName} must be submitted by a parent until this Player ID is claimed.`
          : 'View only — a parent must submit contest videos until you claim your email.',
      );
      return;
    }
    if (!canWriteThisAge) {
      setUploadError(ageMismatchMessage);
      return;
    }
    if (missingDob) {
      setUploadError(
        'Add a date of birth on this profile first. Age category is calculated from date of birth.',
      );
      return;
    }

    if (!canBrowseSubmit) {
      setUploadError('Complete Self Evaluation and confirm you are ready before submitting.');
      return;
    }

    const contest = allContests.find((c) => c.id === contestId);
    const file = pendingFiles[contestId];
    if (!contest || !file || !playerId) {
      setUploadError('Browse a video first, then Submit.');
      return;
    }

    const recordedDate =
      pendingRecordedDates[contestId] || submissionsByCatalog[contestId]?.recordedDate || '';
    if (!String(recordedDate).trim()) {
      setUploadError('Select the recorded date for this video first.');
      return;
    }
    if (recordedDate > todayIsoDate()) {
      setUploadError('Recorded date cannot be in the future.');
      return;
    }
    if (
      countContestVideosOnDate(submissionsByCatalog, recordedDate, contestId) >= MAX_CONTEST_VIDEOS_PER_DATE
    ) {
      setUploadError(CONTEST_TWO_PER_DAY_MESSAGE);
      return;
    }
    const counts = parseSelfAttemptCounts(
      pendingSelfCounts[contestId]?.attempts,
      pendingSelfCounts[contestId]?.successes,
    );
    if (!counts.ok) {
      setUploadError(counts.message);
      return;
    }

    setUploadError(null);
    setPendingConsentContestId(contestId);
    setConsentOpen(true);
  };

  const performVideoUpload = async (contestId: string) => {
    const contest = allContests.find((c) => c.id === contestId);
    const file = pendingFiles[contestId];
    const counts = parseSelfAttemptCounts(
      pendingSelfCounts[contestId]?.attempts,
      pendingSelfCounts[contestId]?.successes,
    );
    if (!contest || !file || !playerId || !counts.ok) return;

    try {
      setUploadingContestId(contestId);
      setUploadError(null);
      await uploadContestPerformanceVideo({
        sportName,
        contest,
        ageCategory,
        playerId,
        playerName,
        userId,
        file,
        recordedDate:
          pendingRecordedDates[contestId] || submissionsByCatalog[contestId]?.recordedDate || '',
        selfAttempts: counts.attempts,
        selfSuccesses: counts.successes,
        tasks: contestTasks,
        consentAttested: true,
      });
      setPendingFiles((prev) => {
        const next = { ...prev };
        delete next[contestId];
        return next;
      });
      setPendingRecordedDates((prev) => {
        const next = { ...prev };
        delete next[contestId];
        return next;
      });
      setPendingSelfCounts((prev) => {
        const next = { ...prev };
        delete next[contestId];
        return next;
      });
      await refreshSubmitted();
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: { message?: string } }; message?: string };
      const message =
        axiosErr.response?.data?.message ||
        (e instanceof Error ? e.message : 'Failed to upload performance video');
      setUploadError(message);
    } finally {
      setUploadingContestId(null);
    }
  };

  const handleConsentConfirm = (record: ContestSelfEvalConsentRecord) => {
    const contestId = pendingConsentContestId;
    setConsentOpen(false);
    setPendingConsentContestId(null);
    if (!contestId || !record.attested) return;
    void performVideoUpload(contestId);
  };



  if (!hasPreview && !catalogLoading) {

    return <Alert severity="info">Contest submission is not available for {sportName} yet.</Alert>;

  }

  if (catalogLoading && !hasContests) {

    return <Alert severity="info">Loading contest catalog…</Alert>;

  }



  const currentQuarterLabel = contestSelfEvalQuarterLabel();
  const profileBar = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
        borderBottom: '2px solid #e2e8f0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
      <Box
        sx={{
          borderBottom: `3px solid ${brandColors.orange}`,
          color: brandColors.navy,
          fontFamily: '"Sora", Arial, sans-serif',
          fontWeight: 800,
          fontSize: { xs: '0.9rem', md: '0.95rem' },
          px: { xs: 1.25, md: 1.5 },
          pt: 0.65,
          pb: 1,
          mb: '-2px',
          lineHeight: 1.3,
        }}
      >
        Video submission
      </Box>
      <Button
        size="small"
        onClick={() => setCompareOpen(true)}
        startIcon={<CompareArrowsIcon sx={{ fontSize: '16px !important' }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          color: brandColors.navy,
          mb: 0.5,
        }}
      >
        Compare with Self Evaluation
      </Button>
      </Box>
      {profileSlot ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.4,
            mb: 0.5,
            borderRadius: 1.5,
            bgcolor: '#fde8dc',
            border: `1px solid ${brandColors.orange}55`,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: '0.68rem',
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: brandColors.orange,
              whiteSpace: 'nowrap',
            }}
          >
            Profile
          </Typography>
          {profileSlot}
        </Box>
      ) : null}
    </Box>
  );

  if (!viewOnly && !selfEvalComplete) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {profileBar}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            p: { xs: 2, md: 2.5 },
            bgcolor: brandColors.white,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 1, fontSize: '1.05rem' }}>
            Finish Self Evaluation before Contest{playerName ? ` — ${playerName}` : ''}
          </Typography>
          <Typography sx={{ color: brandColors.slate, lineHeight: 1.6, mb: 1.25 }}>
            EncycGames uses a lean prep path: Benchmark → Learn → Self Evaluation (ideally with coach or parent
            feedback) → then contest videos. Leaderboard is secondary — preparation comes first.
          </Typography>
          <Typography sx={{ color: brandColors.slate, lineHeight: 1.6, mb: 1.5, fontSize: '0.92rem' }}>
            Record values for every Foundation skill on the Self Evaluation tab. You can draft self-eval without
            logging in; sign in when you want to save permanently. Without a coach, complete self-eval yourself —
            that still unlocks Contest.
          </Typography>
          <Typography sx={{ color: brandColors.slate, mb: 2, fontSize: '0.92rem' }}>
            Progress for {ageCategory}: <strong>{filledCount}</strong> / <strong>{contestIds.length || '—'}</strong>{' '}
            skills recorded.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant="contained"
              onClick={() => onGoToSelfEval?.()}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.violet }}
            >
              Go to Self Evaluation
            </Button>
          </Stack>
        </Paper>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Browse and video submit stay closed until every Foundation skill has a self-eval value for this age.
          Benchmark and Learn remain open.
        </Alert>
        <ContestResultsCompareDialog
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          playerName={playerName}
          ageCategory={ageCategory}
          rows={compareRows}
        />
      </Box>
    );
  }

  if (!viewOnly && !contestUnlocked) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {profileBar}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${brandColors.orange}55`,
            borderRadius: 2,
            p: { xs: 2, md: 2.5 },
            bgcolor: brandColors.white,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 1, fontSize: '1.05rem' }}>
            Are you ready for the contest and to submit videos{playerName ? ` for ${playerName}` : ''}?
          </Typography>
          <Typography sx={{ color: brandColors.slate, lineHeight: 1.6, mb: 1.25 }}>
            Self Evaluation for {ageCategory} is complete ({filledCount} skills). Coach/parent feedback on those
            notes helps a lot. Submitting without prep often yields low scores — but after self-eval you may still
            submit even if grades are still developing.
          </Typography>
          <Typography sx={{ color: brandColors.slate, lineHeight: 1.6, mb: 2, fontSize: '0.92rem' }}>
            Confirm only when you (and ideally your coach/parent) are ready to film and upload.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant="contained"
              color="secondary"
              onClick={confirmReady}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Yes — I&apos;m ready to submit{playerName ? ` for ${playerName}` : ''}
            </Button>
          </Stack>
        </Paper>
        <ContestResultsCompareDialog
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          playerName={playerName}
          ageCategory={ageCategory}
          rows={compareRows}
        />
      </Box>
    );
  }

  return (

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {profileBar}

      {uploadError && (

        <Alert severity="error" onClose={() => setUploadError(null)}>

          {uploadError}

        </Alert>

      )}

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {CONTEST_TWO_PER_DAY_MESSAGE}. Enter total attempts and successes for each skill — reviewers use these
        self-verified counts.
      </Alert>



      <ContestFrameShell tone="primary" integratedTitle={playerName ? `${foundationLabel} · ${playerName}` : foundationLabel} ageBadge={ageCategory} quarterBadge={currentQuarterLabel} titleAccent={brandColors.orange} frameHeight={{ xs: 'min(62vh, 520px)', md: 'min(68vh, 580px)', lg: 'calc(100dvh - 340px)' }}>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ContestSelfEvalScorecardPanel

          variant="contest"

          categoryGroups={categoryGroups}

          ageCategory={ageCategory}

          genderKey={genderKey}

          entries={contestEntries}

          submittedContestIds={submittedIds}

          submittedVideoUrls={submittedVideoUrls}

          pendingVideoFiles={pendingFiles}

          uploadingContestId={uploadingContestId}

          onValueChange={() => undefined}

          onAttemptsChange={(contestId, value) => handleSelfCountChange(contestId, 'attempts', value)}

          onSuccessesChange={(contestId, value) => handleSelfCountChange(contestId, 'successes', value)}

          onDateChange={handleRecordedDateChange}

          onSelfNotesChange={handleSelfNotesChange}

          onBrowseClick={handleBrowseClick}

          onVideoSelected={handleVideoSelected}

          onSubmitVideo={(contestId) => void handleSubmitVideo(contestId)}
          readOnly={viewOnly || !canWriteThisAge || missingDob}

        />

        </Box>

      </ContestFrameShell>

      {consentOpen && pendingConsentContestId ? (
        <ContestSelfEvalConsentDialog
          open
          action="video"
          sportName={sportName}
          ageCategory={ageCategory}
          role={resolveSelfEvalConsentRole(null, actingKind)}
          skills={allContests
            .map((contest, index) => ({ contestId: contest.id, title: contest.title, index: index + 1 }))
            .filter((skill) => skill.contestId === pendingConsentContestId)}
          guidelineContest={allContests.find((contest) => contest.id === pendingConsentContestId) || allContests[0]}
          playerName={playerName}
          onCancel={() => {
            setConsentOpen(false);
            setPendingConsentContestId(null);
          }}
          onConfirm={handleConsentConfirm}
        />
      ) : null}

      <ContestResultsCompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        playerName={playerName}
        ageCategory={ageCategory}
        rows={compareRows}
      />

    </Box>

  );

};



export default ContestSubmissionTabPanel;


